import React from 'react'
import { useCreate, useUpdate, useList, useInvalidate } from '@refinedev/core'
import { App, Button, DatePicker, Form, Input, InputNumber, Select, Switch, Typography } from 'antd'
import dayjs from 'dayjs'
import { Link } from 'react-router'
import { brand } from '../../theme/brand'
import { SectionCard, Pill, TONES } from '../../theme/listkit'
import { BadgeCheck } from '../../components/icons'
import { ImageUploadField } from '../../components/ImageUploadField'
import { monthLabel, upcomingMonths, addMonths, spotlightShareText, STORY_QUESTIONS } from './botm'
import type { EligibilityRow } from './EligibilityTab'
import { useJaguarOwnedSwitches } from '../jaguar_owned_controls/useJaguarOwnedSwitches'

const { Text } = Typography

export interface SpotlightBusiness {
  id: string
  name: string
  owner?: { name: string | null; class_year: string | null } | null
}

export interface StoryItem {
  question: string
  answer: string
}

export interface SpotlightRecord {
  id: string
  business_id: string
  month: string
  published_at: string | null
  headline: string
  pull_quote: string | null
  story: StoryItem[] | null
  cover_url: string | null
  perk_title: string | null
  total_score: number | null
  business?: SpotlightBusiness | null
}

const DEFAULT_STORY: StoryItem[] = STORY_QUESTIONS.map((q) => ({ question: q, answer: '' }))

const COVER_URL_PATTERN = /^https?:\/\//i
const PUBLISH_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/**
 * The stored story, topped up with any standard question it's missing — a
 * spotlight saved after answering only question 1 must still show questions
 * 2–5 (with empty answers) on the next edit, not silently drop them. Matched
 * by exact question text, since the question wording is editable.
 */
function mergeStory(stored: StoryItem[] | null | undefined): StoryItem[] {
  if (!stored || stored.length === 0) return DEFAULT_STORY
  const askedAlready = new Set(stored.map((s) => s.question))
  const missing = STORY_QUESTIONS.filter((q) => !askedAlready.has(q)).map((q) => ({ question: q, answer: '' }))
  return [...stored, ...missing]
}

/** The 1st of `month` at 09:00 LOCAL time, as an ISO string — the seed value
 * for `published_at` when "Publish on a date" is switched on, and the
 * reference point for detecting whether a later month change should re-seed
 * it (see the `formMonth` effect below). */
function defaultPublishedAt(month: string): string {
  return dayjs(month, 'YYYY-MM-DD').startOf('month').hour(9).minute(0).second(0).millisecond(0).toISOString()
}

/**
 * Turns a raw create/update failure into copy an admin can act on.
 *
 * `code: '23505'` is Postgres's unique-violation code — `business_spotlights`
 * has exactly one unique constraint besides its primary key (`month`, see
 * `20260923120200_jaguar_owned_spotlight.sql:88`), so a 23505 here always
 * means "this month already has a spotlight". The Supabase data provider
 * spreads the original PostgrestError onto the thrown object before setting
 * `message`/`statusCode` (`@refinedev/supabase`'s `handleError`), so `code`
 * survives untouched as the string `'23505'`.
 */
function saveErrorMessage(e: unknown, month: string): string {
  const err = e as { code?: string; message?: string } | Error | undefined
  const code = typeof err === 'object' && err !== null ? (err as { code?: string }).code : undefined
  const rawMessage =
    err instanceof Error ? err.message : typeof err === 'object' && err !== null ? (err as { message?: string }).message : undefined
  if (code === '23505') {
    return `There's already a spotlight for ${monthLabel(month)}. Edit it in the table instead.`
  }
  return rawMessage ?? 'Something went wrong. Try again.'
}

interface SpotlightFormProps {
  /** Omit (or pass null) to create a new spotlight; pass the row to edit one. */
  initial?: SpotlightRecord | null
  /** The month a NEW spotlight defaults to — the console's currently selected month. */
  month: string
  onDone: () => void
}

/**
 * Create/edit form for one Business of the Month spotlight, embedded in
 * ScheduleTab's Drawer. `published_at` drives draft/scheduled/live exactly like
 * releases (see ReleaseFormFields.tsx): it stays a REGISTERED Form.Item at all
 * times (via `hidden`, not unmounting), so turning "Publish on a date" off always
 * submits an explicit `published_at: null` instead of silently dropping the
 * field. Turning it on seeds the 1st of the selected month at 09:00 LOCAL time
 * (the association runs on Miami/Eastern) — a starting point the admin can still
 * move with the picker. Changing the Month afterward re-seeds that default IF
 * the current value is still exactly the previous month's default (untouched by
 * the admin); a hand-edited date is left alone but flagged with an inline
 * warning when its month no longer matches the spotlight's.
 *
 * Save goes straight through `business_spotlights`' own guard trigger
 * (eligibility + the 24-month, month-relative cooldown —
 * backend/supabase/migrations/20260923120200_jaguar_owned_spotlight.sql) and
 * its CHECK constraints (headline non-blank/≤120, pull_quote ≤200, perk_title
 * ≤80, cover_url `^https?://`). Form rules mirror those CHECKs so most
 * failures never reach the server; `error.message` (or the friendly 23505
 * mapping in `saveErrorMessage`) surfaces whatever does, the same pattern as
 * class_year_change_requests/show.tsx. `successNotification`/`errorNotification`
 * are disabled on both mutations (precedent: PromoteAdmin.tsx) so this shows
 * exactly one toast, not Refine's default plus this component's own.
 */
export function SpotlightForm({ initial, month, onDone }: SpotlightFormProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { mutate: create } = useCreate()
  const { mutate: update } = useUpdate()
  const invalidate = useInvalidate()
  const [saving, setSaving] = React.useState(false)

  // Perks switch (Task C4, §4 of the plan): while perks are off, the perk
  // title field is hidden entirely and its key is left out of the save
  // payload — see `perksWaived` below and the payload build in `save()`.
  //
  // `perksWaived` mirrors C1's `jaguar_owned_perks_waived()`, which treats a
  // MISSING row as NOT waived (the field stays required) — only an
  // explicit, disabled row waives it. It's also false while the switches
  // are still loading or failed to load, so the form shows its ordinary
  // perk field rather than guessing.
  const { byKey: switches, isLoading: switchesLoading, isError: switchesError } = useJaguarOwnedSwitches()
  const perksRow = switches.get('jaguar_owned_perks_enabled')
  const perksWaived = !switchesLoading && !switchesError && !!perksRow && !perksRow.enabled

  const publishedAt = Form.useWatch('published_at', form) as string | null | undefined
  const publishToggle = Boolean(publishedAt)
  const formMonth = (Form.useWatch('month', form) as string | undefined) ?? initial?.month ?? month

  // Re-seed published_at when the month changes AFTER "Publish on a date" was
  // switched on — but only while the current value is still exactly the
  // PREVIOUS month's auto-seeded default (untouched by the admin). A
  // hand-edited value is left alone; `publishedMonthMismatch` below flags it
  // instead.
  const prevMonthRef = React.useRef(formMonth)
  React.useEffect(() => {
    const prevMonth = prevMonthRef.current
    prevMonthRef.current = formMonth
    if (prevMonth === formMonth || !publishToggle) return
    const current = form.getFieldValue('published_at') as string | null | undefined
    if (current && current === defaultPublishedAt(prevMonth)) {
      form.setFieldValue('published_at', defaultPublishedAt(formMonth))
    }
    // formMonth is the only real trigger — form/publishToggle read the latest
    // value already and don't need to re-run this on their own change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMonth])

  const publishedMonthMismatch = React.useMemo(() => {
    if (!publishToggle || !publishedAt) return false
    return dayjs(publishedAt).format('YYYY-MM') !== dayjs(formMonth, 'YYYY-MM-DD').format('YYYY-MM')
  }, [publishToggle, publishedAt, formMonth])

  const { result: eligResult } = useList<EligibilityRow>({
    resource: 'business_spotlight_eligibility',
    pagination: { pageSize: 500 },
  })
  const businessOptions = React.useMemo(() => {
    const eligible = (eligResult?.data ?? []).filter((r) => r.eligible === true)
    const options = eligible.map((r) => ({ value: r.business_id, label: r.name ?? r.business_id }))
    // When editing, the business already on the spotlight must stay selectable
    // even if it's now in cooldown (it just won) or otherwise no longer eligible.
    if (initial?.business_id && !options.some((o) => o.value === initial.business_id)) {
      options.push({ value: initial.business_id, label: initial.business?.name ?? initial.business_id })
    }
    return options
  }, [eligResult, initial])

  const monthOptions = React.useMemo(() => {
    const base = upcomingMonths(addMonths(month, -1), 6)
    if (initial?.month && !base.includes(initial.month)) return [...base, initial.month]
    return base
  }, [month, initial])

  const initialValues = React.useMemo(
    () => ({
      business_id: initial?.business_id,
      month: initial?.month ?? month,
      headline: initial?.headline ?? 'Business of the Month',
      pull_quote: initial?.pull_quote ?? undefined,
      story: mergeStory(initial?.story),
      cover_url: initial?.cover_url ?? undefined,
      perk_title: initial?.perk_title ?? undefined,
      total_score: initial?.total_score ?? undefined,
      published_at: initial?.published_at ?? null,
    }),
    [initial, month],
  )

  function togglePublish(checked: boolean) {
    form.setFieldValue('published_at', checked ? defaultPublishedAt(formMonth) : null)
  }

  function save() {
    form
      .validateFields()
      .then((values) => {
        const coverUrl = (values.cover_url as string | undefined)?.trim()
        if (coverUrl && !COVER_URL_PATTERN.test(coverUrl)) {
          form.setFields([{ name: 'cover_url', errors: ['Cover image URL must start with http:// or https://'] }])
          return
        }

        setSaving(true)
        const story = ((values.story as StoryItem[]) ?? []).filter(
          (item) => item.answer && item.answer.trim() !== '',
        )
        const payload: Record<string, unknown> = {
          business_id: values.business_id,
          month: values.month,
          headline: values.headline,
          pull_quote: values.pull_quote || null,
          story,
          cover_url: coverUrl || null,
          total_score: values.total_score ?? null,
          published_at: values.published_at ?? null,
        }
        // The spotlight's own perk_title field is hidden while perks are
        // off, so it's left OUT of the payload entirely rather than
        // re-sent from `initial.perk_title` — a partial update only
        // touches the keys present in it, so omitting the key leaves
        // whatever's already on the row untouched even if it changed
        // concurrently (another admin, or perks flipping back on) between
        // this form loading and Save being clicked.
        if (!perksWaived) {
          payload.perk_title = values.perk_title || null
        }
        const onSettled = () => setSaving(false)
        const onError = (e: unknown) => {
          onSettled()
          message.error(saveErrorMessage(e, values.month as string))
        }
        const onSuccess = () => {
          onSettled()
          // A save can change who's eligible (a winner enters its 24-month
          // cooldown), so the console's other eligibility reads must refetch.
          invalidate({ resource: 'business_spotlight_eligibility', invalidates: ['list'] })
          message.success('Spotlight saved.')
          onDone()
        }
        const mutationOpts = { successNotification: false as const, errorNotification: false as const }
        if (initial?.id) {
          update(
            { resource: 'business_spotlights', id: initial.id, values: payload, ...mutationOpts },
            { onSuccess, onError },
          )
        } else {
          create({ resource: 'business_spotlights', values: payload, ...mutationOpts }, { onSuccess, onError })
        }
      })
      .catch(() => {
        // AntD's own field-level error messages already show what's missing;
        // this just stops a failed-validation rejection from going unhandled.
      })
  }

  return (
    <div>
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item label="Business" name="business_id" rules={[{ required: true, message: 'Choose a business' }]}>
          <Select
            data-testid="spotlight-business"
            options={businessOptions}
            showSearch
            optionFilterProp="label"
            placeholder="Choose a business"
          />
        </Form.Item>
        <Form.Item label="Month" name="month" rules={[{ required: true, message: 'Choose a month' }]}>
          <Select options={monthOptions.map((m) => ({ value: m, label: monthLabel(m) }))} />
        </Form.Item>
        <Form.Item
          label="Headline"
          name="headline"
          rules={[
            { required: true, whitespace: true, message: 'Headline is required' },
            { max: 120, message: 'Headline must be 120 characters or fewer' },
          ]}
        >
          <Input maxLength={120} />
        </Form.Item>
        <Form.Item
          label="Pull quote"
          name="pull_quote"
          rules={[{ max: 200, message: 'Pull quote must be 200 characters or fewer' }]}
        >
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>

        <Text
          type="secondary"
          style={{ display: 'block', fontFamily: brand.fSans, fontSize: 12, marginBottom: 8 }}
        >
          Story — edit the question wording if needed; a question left with no answer is dropped on save.
        </Text>
        <Form.List name="story">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item {...rest} name={[name, 'question']} label="Question" style={{ marginBottom: 6 }}>
                      <Input />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'answer']} label="Answer" style={{ marginBottom: 0 }}>
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </div>
                  <Button danger size="small" onClick={() => remove(name)} style={{ marginTop: 28 }}>
                    Remove
                  </Button>
                </div>
              ))}
              <Button onClick={() => add({ question: '', answer: '' })} block>
                + Add question
              </Button>
            </>
          )}
        </Form.List>

        <ImageUploadField
          form={form}
          name="cover_url"
          label="Cover image URL"
          tooltip="Must start with http:// or https://"
          folder="business-spotlights"
          currentUrl={initial?.cover_url}
        />

        {perksWaived ? (
          <Text
            type="secondary"
            style={{ display: 'block', fontFamily: brand.fSans, fontSize: 12, marginBottom: 16 }}
          >
            Member perks are off, so there&apos;s no perk title to set. An existing perk title is kept.
          </Text>
        ) : (
          <Form.Item
            label="Perk title"
            name="perk_title"
            rules={[{ max: 80, message: 'Perk title must be 80 characters or fewer' }]}
          >
            <Input maxLength={80} />
          </Form.Item>
        )}
        <Form.Item label="Total score" name="total_score">
          <InputNumber min={0} max={100} step={0.1} style={{ width: 160 }} />
        </Form.Item>

        <Form.Item label="Publish on a date">
          <Switch
            checked={publishToggle}
            checkedChildren="On"
            unCheckedChildren="Off"
            onChange={togglePublish}
          />
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              Off keeps this a draft only admins can see. On publishes at the date below, which also
              decides when it goes live and when it counts as past.
            </Text>
          </div>
        </Form.Item>
        <Form.Item
          label="Published date (local time)"
          name="published_at"
          hidden={!publishToggle}
          getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
          normalize={(value) => (value && dayjs.isDayjs(value) ? value.toISOString() : value)}
          extra={
            publishedMonthMismatch ? (
              <span style={{ color: '#a4452f', fontWeight: 600 }}>
                Publishes in a different month than the spotlight.
              </span>
            ) : undefined
          }
        >
          <DatePicker showTime format={PUBLISH_TIME_FORMAT} style={{ width: 280 }} />
        </Form.Item>

        <Button type="primary" data-testid="spotlight-save" loading={saving} disabled={saving} onClick={save}>
          Save
        </Button>
      </Form>

      {initial ? <ShareChecklist record={initial} /> : null}
    </div>
  )
}

/**
 * The share checklist — what still needs a human, once a spotlight exists.
 * `spotlightShareText` (from @muaa/shared, re-exported via botm.ts) is the ONE
 * place this WhatsApp copy is composed, so admin and mobile can never drift.
 */
function ShareChecklist({ record }: { record: SpotlightRecord }) {
  const { message } = App.useApp()
  const businessName = record.business?.name ?? null
  const ownerName = record.business?.owner?.name ?? null
  const canCopy = Boolean(businessName && ownerName)
  const shareText = spotlightShareText({
    month: record.month,
    businessName: businessName ?? 'the business',
    ownerName: ownerName ?? 'the owner',
    classYear: record.business?.owner?.class_year ?? null,
  })

  async function copyText() {
    try {
      await navigator.clipboard.writeText(shareText)
      message.success('Copied.')
    } catch {
      message.error("Couldn't copy — select and copy the text manually.")
    }
  }

  return (
    <SectionCard title="Share checklist" style={{ maxWidth: 720, marginTop: 20 }}>
      <ChecklistRow label="Home card and feed card — automatic when the spotlight is live" status="done" />
      <ChecklistRow label="Tell members in the app" status="action">
        <Link to="/send-push">
          <Button size="small">Open Send Push</Button>
        </Link>
        <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub, marginTop: 6 }}>
          Push notifications are off in production, so members see the in-app notification only.
        </div>
      </ChecklistRow>
      <ChecklistRow label="WhatsApp text for class reps" status="action">
        <Button size="small" data-testid="spotlight-copy-text" disabled={!canCopy} onClick={copyText}>
          Copy text
        </Button>
        {!canCopy ? (
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub, marginTop: 6 }}>
            Missing business or owner name.
          </div>
        ) : null}
      </ChecklistRow>
      <ChecklistRow label="Newsletter lead story · printed certificate" status="todo" isLast />
    </SectionCard>
  )
}

function ChecklistRow({
  label,
  status,
  children,
  isLast,
}: {
  label: string
  status: 'done' | 'action' | 'todo'
  children?: React.ReactNode
  isLast?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 14,
        padding: '12px 0',
        borderBottom: isLast ? 'none' : '1px solid ' + brand.cardLine,
      }}
    >
      <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13, color: brand.ink, flex: 1 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
        {status === 'done' ? <BadgeCheck size={18} color={TONES.green.fg} /> : null}
        {status === 'todo' ? <Pill tone="neutral">To do</Pill> : null}
        {children}
      </div>
    </div>
  )
}
